/* eslint-disable no-buffer-constructor */
import axios from "axios";
import fileDownload from "react-file-download";
import { InvalidCredentialsError } from "../../../domain/errors/invalid-credentials-error";
import { UnexpectedError } from "../../../domain/errors/unexpected-error";
import { FilterModel } from "../../../domain/models/enum";
import {
  FiscalNote,
  FiscalNoteModel,
  FiscalNoteResponseModel,
} from "../../../domain/models/fiscal-note";
import Authentication from "../../../infra/cognito/authentication";
import { HttpClient, HttpStatusCode } from "../../protocols/http/http-client";

export class RemoteFiscalNote implements FiscalNote {
  constructor(
    private readonly url: string,
    private readonly httpPostClient: HttpClient,
    private readonly baseUrl: string | undefined
  ) {}

  async load(params?: FilterModel): Promise<FiscalNoteResponseModel> {
    const httpResponse = await this.httpPostClient.request({
      url: `${this.url}`,
      method: "get",
      params,
    });

    switch (httpResponse.statusCode) {
      case HttpStatusCode.ok:
        return httpResponse.body;
      case HttpStatusCode.unauthorized:
        throw new InvalidCredentialsError();
      case HttpStatusCode.badRequest:
        throw httpResponse.body;
      default:
        throw new UnexpectedError();
    }
  }

  async create(body: FiscalNoteModel): Promise<FiscalNoteModel> {
    const httpResponse = await this.httpPostClient.request({
      url: this.url,
      method: "post",
      body,
    });

    switch (httpResponse.statusCode) {
      case HttpStatusCode.created:
        return httpResponse.body;
      case HttpStatusCode.unauthorized:
        throw new InvalidCredentialsError();
      case HttpStatusCode.conflict:
        throw httpResponse.body;
      case HttpStatusCode.badRequest:
        throw httpResponse.body;
      case HttpStatusCode.serverError:
        throw httpResponse.body;
      default:
        throw new UnexpectedError();
    }
  }

  async delete(id: number): Promise<FiscalNoteModel> {
    const httpResponse = await this.httpPostClient.request({
      url: `${this.url}/${id}`,
      method: "delete",
    });
    switch (httpResponse.statusCode) {
      case HttpStatusCode.ok:
        return httpResponse.body;
      case HttpStatusCode.unauthorized:
        throw new InvalidCredentialsError();
      case HttpStatusCode.conflict:
        throw httpResponse.body;
      case HttpStatusCode.badRequest:
        throw httpResponse.body;
      default:
        throw new UnexpectedError();
    }
  }

  async cancel(id: number): Promise<FiscalNoteModel> {
    const httpResponse = await this.httpPostClient.request({
      url: `${this.url}/cancel/${id}`,
      method: "get",
    });
    switch (httpResponse.statusCode) {
      case HttpStatusCode.ok:
        return httpResponse.body;
      case HttpStatusCode.unauthorized:
        throw new InvalidCredentialsError();
      case HttpStatusCode.conflict:
        throw httpResponse.body;
      case HttpStatusCode.badRequest:
        throw httpResponse.body;
      default:
        throw new UnexpectedError();
    }
  }

  async cancelRecurrence(id: number): Promise<FiscalNoteModel> {
    const httpResponse = await this.httpPostClient.request({
      url: `${this.url}/cancel-recurrence/${id}`,
      method: "get",
    });
    switch (httpResponse.statusCode) {
      case HttpStatusCode.ok:
        return httpResponse.body;
      case HttpStatusCode.unauthorized:
        throw new InvalidCredentialsError();
      case HttpStatusCode.conflict:
        throw httpResponse.body;
      case HttpStatusCode.badRequest:
        throw httpResponse.body;
      default:
        throw new UnexpectedError();
    }
  }

  async loadById(id: number): Promise<FiscalNoteModel> {
    const httpResponse = await this.httpPostClient.request({
      url: `${this.url}/${id}`,
      method: "get",
    });

    switch (httpResponse.statusCode) {
      case HttpStatusCode.ok:
        return httpResponse.body;
      case HttpStatusCode.unauthorized:
        throw new InvalidCredentialsError();
      case HttpStatusCode.badRequest:
        throw httpResponse.body;
      default:
        throw new UnexpectedError();
    }
  }

  async update(
    id: number | undefined,
    body: FiscalNoteModel
  ): Promise<FiscalNoteModel> {
    const httpResponse = await this.httpPostClient.request({
      url: `${this.url}/${id}`,
      method: "put",
      body,
    });

    switch (httpResponse.statusCode) {
      case HttpStatusCode.ok:
        return httpResponse.body;
      case HttpStatusCode.unauthorized:
        throw new InvalidCredentialsError();
      case HttpStatusCode.badRequest:
        throw httpResponse.body;
      default:
        throw new UnexpectedError();
    }
  }

  async loadAll(): Promise<FiscalNoteModel[]> {
    const httpResponse = await this.httpPostClient.request({
      url: `${this.url}/all`,
      method: "get",
    });

    switch (httpResponse.statusCode) {
      case HttpStatusCode.ok:
        return httpResponse.body;
      case HttpStatusCode.unauthorized:
        throw new InvalidCredentialsError();
      case HttpStatusCode.badRequest:
        throw httpResponse.body;
      default:
        throw new UnexpectedError();
    }
  }

  async preview(params?: FilterModel): Promise<any> {
    const token = await Authentication.getToken();
    const resp = await axios.get(`${this.baseUrl}/${this.url}/export`, {
      responseType: "blob",
      params,
      headers: {
        Authorization: token,
        accept: "*/*",
      },
    });
    // fileDownload(resp.data, `Nota Fiscal - ${new Date().getTime()}.${params?.type}`)
    // const url = URL.createObjectURL(resp.data)
    
    return resp.data;
  }

  async exportNote(params?: FilterModel): Promise<any> {
    const token = await Authentication.getToken();
    const resp = await axios.get(`${this.baseUrl}/${this.url}/export`, {
      responseType: "blob",
      params,
      headers: {
        Authorization: token,
        accept: "*/*",
      },
    });
    fileDownload(
      resp.data,
      `Nota Fiscal - ${new Date().getTime()}.${params?.type}`
    );
  }
}
